import plgDiscoveryBannerAPI from 'api/plgDiscoveryBanner'
import { ofType } from 'redux-observable'
import { EMPTY, merge, of } from 'rxjs'
import {
  catchError,
  ignoreElements,
  map,
  mergeMap,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators'

import { combineEpicsIsolated } from 'utils/epicRegistry'

import { FETCH_APP_DATA_ASYNC } from 'containers/App/constants'
import { SUBMIT_CUSTOMIZED_AUTO_SYNC_ASYNC } from 'containers/CustomizedAutoSync/constants'
import { AS_GD_ASYNC, AS_O365_ASYNC } from 'containers/Mailboxes/constants'
import {
  QUICK_INTEGRATION_CONTINUE,
  QUICK_INTEGRATION_SAVE_ASYNC,
} from 'containers/Settings/EmailServer/QuickIntegration/constants'

import { fetchDemoBannerStatusError, fetchDemoBannerStatusSuccess } from './actions'
import { DISMISS_DISCOVERY_BANNER, FETCH_DISCOVERY_BANNER_ASYNC } from './constants'

const fetchDemoBannerStatus = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      FETCH_APP_DATA_ASYNC.INITIATE,
      AS_O365_ASYNC.SUCCESS,
      AS_GD_ASYNC.SUCCESS,
      SUBMIT_CUSTOMIZED_AUTO_SYNC_ASYNC.SUCCESS,
      QUICK_INTEGRATION_SAVE_ASYNC.SUCCESS,
      QUICK_INTEGRATION_CONTINUE
    ),
    mergeMap(() =>
      api.accounts.getDemoBannerStatus(state$.value.partnerOptions.selectedCompany?.value).pipe(
        map((response) => fetchDemoBannerStatusSuccess(response)),
        catchError((error) => of(epicsErrorHandler(fetchDemoBannerStatusError, error))),
        takeUntil(action$.pipe(ofType(FETCH_APP_DATA_ASYNC.CANCEL)))
      )
    )
  )

// Discovery banner — fetched once on app-data load via the epic/redux flow
// (consistent with the PLG credit-notifications snapshot; no focus polling), and
// again on every FETCH_DISCOVERY_BANNER_ASYNC.INITIATE (dispatched on top-level
// route changes while the banner is showing — IC-49444: credits exhausted
// mid-session must hide the banner on navigation, not only on refresh). The
// banner only transitions to hidden: finishing the first investigation flips a
// permanent backend flag, the X persists a session dismissal (IC-49457: "Try it
// now" does not dismiss), and useFirstInvestigationOnboarding re-syncs visibility
// from any later GET of the same endpoint. switchMap so overlapping fetches
// don't race.
export const fetchDiscoveryBanner = (action$) =>
  merge(
    action$.pipe(ofType(FETCH_APP_DATA_ASYNC.SUCCESS), take(1)),
    action$.pipe(ofType(FETCH_DISCOVERY_BANNER_ASYNC.INITIATE))
  ).pipe(
    switchMap(() =>
      plgDiscoveryBannerAPI.getState().pipe(
        map((response) => FETCH_DISCOVERY_BANNER_ASYNC.onSuccess(response)),
        // Abort of an in-flight GET (switchMap on a second INITIATE) lands here.
        // The reducer keeps last-known visibility so that does not flash-hide
        // the banner; SUCCESS with showDiscoveryBanner:false is still the hide.
        catchError((error) => of(FETCH_DISCOVERY_BANNER_ASYNC.onError(error)))
      )
    )
  )

// Persist the session dismissal server-side (fire-and-forget); the reducer has
// already hidden the banner optimistically. The request lives inside the stream
// with a catch so a failed dismissal can never error the epic — emits nothing
// on success or failure.
const dismissDiscoveryBanner = (action$) =>
  action$.pipe(
    ofType(DISMISS_DISCOVERY_BANNER),
    mergeMap(() =>
      plgDiscoveryBannerAPI.dismissSession().pipe(
        ignoreElements(),
        catchError(() => EMPTY)
      )
    )
  )

export default combineEpicsIsolated(
  fetchDemoBannerStatus,
  fetchDiscoveryBanner,
  dismissDiscoveryBanner
)
